import {
  validateArrayField,
  validateOptionalPrimitiveField,
} from 'utils/general';
import {ApplicationType} from './applicationData';

export const SECTION_TYPES = ['card', 'faq', 'carousel', 'richtext', 'video'] as const;

type ApplicationSectionTypes = (typeof SECTION_TYPES)[number];

export interface BaseItem {
  title: string;
  type: ApplicationSectionTypes;
  index: number;
}

export interface CardItem extends BaseItem {
  type: 'card';
  color?: string;
  textColor?: string;
  subtitle?: string;
  message?: string;
  scrollText?: string;
}

export interface CarouselQuote {
  id: string;
  quote: string;
  author: string;
}

export interface CarouselItem extends BaseItem {
  type: 'carousel';
  defaultAuthor?: string;
  items?: CarouselQuote[];
}

export interface RichTextItem extends BaseItem {
  type: 'richtext';
  content?: string;
}

export interface FAQQuestion {
  id: string;
  question: string;
  answer: string;
}

export interface FaqItem extends BaseItem {
  type: 'faq';
  questions: FAQQuestion[];
}

export interface VideoItem extends BaseItem {
  type: 'video';
  /** A YouTube watch/short/embed URL or a plain YouTube video id. */
  youtubeUrl?: string;
}

export type AboutSectionItemTypes =
  | 'card'
  | 'carousel'
  | 'richtext'
  | 'faq'
  | 'video';

export type AboutSectionItem =
  | CardItem
  | CarouselItem
  | RichTextItem
  | FaqItem
  | VideoItem;

/**
 * Type-level mapping from AboutSectionItemTypes to their corresponding card data types.
 * This is used for type inference in generic contexts.
 */
export type AboutSectionTypeToItemTypeMap = {
  card: CardItem;
  carousel: CarouselItem;
  richtext: RichTextItem;
  faq: FaqItem;
  video: VideoItem;
};

export type ItemTypeForAboutSection<T extends AboutSectionItemTypes> =
  AboutSectionTypeToItemTypeMap[T];

export interface SectionItems<T extends BaseItem> {
  [itemId: string]: T;
}

export type AboutSections = {
  [K in AboutSectionItemTypes]?: SectionItems<ItemTypeForAboutSection<K>>;
}

// Shared props interface for rich text components
export interface RichTextComponentProps {
  itemId: string;
  courseId: string;
  editorType: ApplicationType;
}

export const validateAboutSections = (data: unknown): AboutSections => {
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    return {};
  }

  const aboutSections: AboutSections = {};
  const aboutSectionData = data as Record<string, unknown>;

  for (const sectionType of Object.keys(aboutSectionData) as AboutSectionItemTypes[]) {
    for (const [itemId, itemData] of Object.entries(aboutSectionData[sectionType])) {
      if (typeof itemId !== 'string' || !itemData) {
        console.warn(`Invalid itemId or itemData for ${itemId}, skipping`);
        continue;
      }

      if (isCardDataType(itemData)) {
        if (!aboutSections[sectionType]) {
          aboutSections[sectionType] = {};
        }
        aboutSections[sectionType][itemId] = itemData as ItemTypeForAboutSection<typeof sectionType>;
      } else {
        console.warn(
          `Failed to validate item data for itemId ${itemId}, skipping`,
        );
      }
    }
  }

  return aboutSections;
};

// Validation functions
export const isCardData = (object: unknown): object is CardItem => {
  if (!object || typeof object !== 'object') {
    return false;
  }
  const card = object as CardItem;

  if (typeof card.title !== 'string' || typeof card.type !== 'string') {
    return false;
  }

  return (
    validateOptionalPrimitiveField(card.color, 'string') &&
    validateOptionalPrimitiveField(card.textColor, 'string') &&
    validateOptionalPrimitiveField(card.subtitle, 'string') &&
    validateOptionalPrimitiveField(card.message, 'string') &&
    validateOptionalPrimitiveField(card.scrollText, 'string')
  );
};

export const isCarouselItem = (object: unknown): object is CarouselQuote => {
  if (!object || typeof object !== 'object') {
    return false;
  }
  const item = object as CarouselQuote;

  if (
    typeof item.id !== 'string' ||
    typeof item.quote !== 'string' ||
    typeof item.author !== 'string'
  ) {
    return false;
  }

  return true;
};

export const isCarouselData = (object: unknown): object is CarouselItem => {
  if (!object || typeof object !== 'object') {
    return false;
  }
  const carousel = object as CarouselItem;

  if (typeof carousel.title !== 'string' || typeof carousel.type !== 'string') {
    return false;
  }

  return (
    validateOptionalPrimitiveField(carousel.defaultAuthor, 'string') &&
    validateArrayField({
      field: carousel.items,
      validateElement: isCarouselItem,
      isOptional: true,
    })
  );
};

export const isRichTextData = (object: unknown): object is RichTextItem => {
  if (!object || typeof object !== 'object') {
    return false;
  }
  const richText = object as RichTextItem;

  if (typeof richText.title !== 'string' || typeof richText.type !== 'string') {
    return false;
  }

  return validateOptionalPrimitiveField(richText.content, 'string');
};

export const isFAQData = (object: unknown): object is FaqItem => {
  if (!object || typeof object !== 'object') {
    return false;
  }
  const faq = object as FaqItem;

  if (typeof faq.title !== 'string' || typeof faq.type !== 'string') {
    return false;
  }

  return validateArrayField({
    field: faq.questions,
    validateElement: isFAQQuestion,
    isOptional: true,
  });
};

export const isVideoData = (object: unknown): object is VideoItem => {
  if (!object || typeof object !== 'object') {
    return false;
  }
  const video = object as VideoItem;

  if (typeof video.title !== 'string' || typeof video.type !== 'string') {
    return false;
  }

  return validateOptionalPrimitiveField(video.youtubeUrl, 'string');
};

export const isFAQQuestion = (object: unknown): object is FAQQuestion => {
  if (!object || typeof object !== 'object') {
    return false;
  }
  const question = object as FAQQuestion;

  if (
    typeof question.id !== 'string' ||
    typeof question.question !== 'string' ||
    typeof question.answer !== 'string'
  ) {
    return false;
  }

  return true;
};

export const isCardDataType = (object: unknown): object is AboutSectionItem => {
  if (!object || typeof object !== 'object') {
    return false;
  }

  const item = object as AboutSectionItem;
  if (typeof item.type !== 'string') {
    return false;
  }

  switch (item.type) {
    case 'card':
      return isCardData(object);
    case 'carousel':
      return isCarouselData(object);
    case 'richtext':
      return isRichTextData(object);
    case 'faq':
      return isFAQData(object);
    case 'video':
      return isVideoData(object);
    default:
      return false;
  }
};

export const isAboutSections = (object: unknown): object is AboutSections => {
  if (!object || typeof object !== 'object') {
    return false;
  }
  const aboutSections = object as AboutSections;

  // Validate each item
  for (const sectionType of Object.keys(aboutSections) as AboutSectionItemTypes[]) {
    for (const [itemId, itemData] of Object.entries(aboutSections[sectionType])) {
      if (typeof itemId !== 'string') {
        return false;
      }
      if (
        !isCardData(itemData) &&
        !isCarouselData(itemData) &&
        !isRichTextData(itemData) &&
        !isFAQData(itemData) &&
        !isVideoData(itemData)
      ) {
        return false;
      }
    }
  }

  return true;
};
